import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, GraduationCap, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const ISO21001LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISO 21001 Lead Auditor Training",
    "description": "ISO 21001 Lead Auditor Training equips professionals with the skills and competence required to audit Educational Organizations Management Systems (EOMS) based on ISO 21001:2018.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy"
    },
    "coursePrerequisites": "Basic knowledge of management systems or education processes recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "In-person",
      "duration": "5 days"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the eligibility for ISO 21001 Lead Auditor training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "There are no strict eligibility criteria, but basic knowledge of management systems or education processes is recommended."
        }
      },
      {
        "@type": "Question",
        "name": "Is ISO 21001 Lead Auditor certification globally valid?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, certification issued by accredited bodies is internationally recognized."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to complete the course?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Typically 4–5 days, including examination."
        }
      },
      {
        "@type": "Question",
        "name": "Can I attend ISO 21001 Lead Auditor training online?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, most providers offer live online training with the same certification value."
        }
      },
      {
        "@type": "Question",
        "name": "Is ISO 21001 mandatory for educational institutions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ISO 21001 is voluntary, but highly recommended to improve quality, credibility, and stakeholder confidence."
        }
      }
    ]
  };

  const courseObjectives = [
    'Understand ISO 21001:2018 requirements in detail',
    'Interpret clauses and controls effectively',
    'Plan and prepare an EOMS audit',
    'Conduct audits using risk-based thinking',
    'Identify nonconformities and improvement opportunities',
    'Prepare professional audit reports',
    'Lead audit teams confidently'
  ];

  const importanceReasons = [
    {
      title: 'Growing Demand in the Education Sector',
      description: 'Educational institutions worldwide are adopting ISO 21001 to demonstrate credibility, quality assurance, and compliance. Certified lead auditors are in high demand for audits, consulting, and implementation projects.',
      icon: TrendingUp
    },
    {
      title: 'Global Career Opportunities',
      description: 'ISO 21001 Lead Auditor certification is internationally recognized, opening doors to opportunities in India and abroad.',
      icon: Globe
    },
    {
      title: 'Professional Credibility',
      description: 'Being a certified Lead Auditor enhances your professional profile and positions you as a subject matter expert in educational quality management systems.',
      icon: Award
    },
    {
      title: 'Value Addition for Institutions',
      description: 'Certified auditors help institutions improve learning processes, increase student satisfaction, enhance governance and transparency, and meet regulatory and accreditation requirements.',
      icon: GraduationCap
    }
  ];

  const targetAudience = [
    'Quality managers in educational institutions',
    'Internal and external auditors',
    'Education consultants and trainers',
    'Compliance officers',
    'School and college administrators',
    'Accreditation and regulatory professionals',
    'ISO consultants looking to expand their portfolio'
  ];

  const trainingInIndiaBenefits = [
    'Cost-effective compared to international locations',
    'Access to experienced trainers',
    'Flexible online and classroom options',
    'Globally accepted certification'
  ];

  const faqs = [
    {
      question: 'What is the eligibility for ISO 21001 Lead Auditor training?',
      answer: 'There are no strict eligibility criteria, but basic knowledge of management systems or education processes is recommended.'
    },
    {
      question: 'Is ISO 21001 Lead Auditor certification globally valid?',
      answer: 'Yes, certification issued by accredited bodies is internationally recognized.'
    },
    {
      question: 'How long does it take to complete the course?',
      answer: 'Typically 4–5 days, including examination.'
    },
    {
      question: 'Can I attend ISO 21001 Lead Auditor training online?',
      answer: 'Yes, most providers offer live online training with the same certification value.'
    },
    {
      question: 'Is ISO 21001 mandatory for educational institutions?',
      answer: 'ISO 21001 is voluntary, but highly recommended to improve quality, credibility, and stakeholder confidence.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="ISO 21001 Lead Auditor Training in India | EOMS Certification"
        description="ISO 21001 Lead Auditor Training equips professionals with skills to audit Educational Organizations Management Systems (EOMS) based on ISO 21001:2018. Globally recognized certification."
        keywords="ISO 21001 lead auditor training, ISO 21001 lead auditor certification, ISO 21001 lead auditor training India, EOMS audit, educational organizations management system, ISO 21001:2018, lead auditor certification, education quality management"
        canonical="/ISO-21001-Lead-Auditor-Training"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block bg-teal-500 px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              INTERNATIONALLY RECOGNIZED CERTIFICATION
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              ISO 21001 Lead Auditor Training in India
            </h1>
            <p className="text-xl md:text-2xl text-teal-100 mb-8 max-w-4xl mx-auto">
              In today's rapidly evolving education sector, quality, learner satisfaction, and continuous improvement are no longer optional—they are essential. ISO 21001 Lead Auditor Training equips professionals with the skills and competence required to audit Educational Organizations Management Systems (EOMS) based on the international standard ISO 21001:2018.
            </p>
            <p className="text-lg text-teal-100 mb-8 max-w-3xl mx-auto">
              Whether you are an education professional, quality manager, consultant, or auditor, ISO 21001 Lead Auditor certification helps you gain global recognition and play a critical role in improving educational institutions.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>4–5 Days Intensive Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6" />
                <span>Global Recognition</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iso-21001']} currentLevel="leadAuditor" />

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-21001" />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* What is ISO 21001 Lead Auditor Training - h2 */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              What is ISO 21001 Lead Auditor Training?
            </h2>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  ISO 21001 Lead Auditor Training is a professional certification program designed to develop the knowledge and skills needed to plan, conduct, manage, and report audits of educational organizations in accordance with ISO 21001 standards.
                </p>
                <p className="text-lg text-gray-700 mb-4">This training follows internationally accepted auditing guidelines such as:</p>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">ISO 19011 (Guidelines for Auditing Management Systems)</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">ISO/IEC 17021-1 (Requirements for Audit and Certification Bodies)</span>
                  </div>
                </div>
                <p className="text-lg text-gray-700">
                  Upon successful completion, participants are eligible to conduct first-party, second-party, and third-party audits for educational institutions.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Course Objectives - h2 */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              ISO 21001 Lead Auditor Training Course Objectives
            </h2>
            <Card className="bg-teal-50 border-teal-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">After completing the training, participants will be able to:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {courseObjectives.map((objective, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{objective}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Why Certification is Important - h3 */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Why ISO 21001 Lead Auditor Certification is Important
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {importanceReasons.map((reason, index) => {
                const IconComponent = reason.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-teal-100 p-3 rounded-lg flex-shrink-0">
                          <IconComponent className="h-6 w-6 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold mb-3 text-gray-900">{index + 1}. {reason.title}</h4>
                          <p className="text-gray-700">{reason.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        </section>

        {/* Who Should Attend - h3 */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 flex items-center gap-3">
              <Users className="h-8 w-8 text-teal-600" />
              Who Should Attend ISO 21001 Lead Auditor Training?
            </h3>
            <Card>
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">This training is ideal for:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  {targetAudience.map((audience, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{audience}</span>
                    </div>
                  ))}
                </div>
                <p className="text-lg text-gray-700 mt-6">
                  No matter your role, if you are involved in education quality, auditing, or management systems, this course is highly relevant.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Training in India - h3 */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              ISO 21001 Lead Auditor Training in India
            </h3>
            <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  India has emerged as a major hub for ISO training and certification. ISO 21001 Lead Auditor Training in India is offered by accredited training providers aligned with PECB, Exemplar Global, BSI, and TUV standards.
                </p>
                <h4 className="text-xl font-bold mb-4 text-gray-900">Benefits of Training in India:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {trainingInIndiaBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                      <CheckCircle className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 font-semibold">{benefit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-700">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Become a Certified ISO 21001 Lead Auditor?
                </h2>
                <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
                  Join professionals who are shaping education quality worldwide. Get certified and make a lasting impact in the education sector.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </div>

      {/* FAQ Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />
    </div>
  );
};
